import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalPortal, ModalOverlay, ModalClose, ModalTitle, ModalDescription } from '@klaro7/ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import { shouldShowLeadPopup } from '../lib/leadPopupVisibility';
import { captureLeadPopup } from '../services/leadCapture.service';

const DISMISS_KEY = 'klaro7_lead_popup_dismissed';
const LOCAL_PREVIEW_ENABLED = import.meta.env.DEV;
// Trigger d'abbandono: scroll vicino al fondo pagina (stile email-catcher kang.it).
// Scatta a >= 90% della distanza scrollabile, solo se la pagina è davvero scrollabile.
const NEAR_BOTTOM_RATIO = 0.9;
const MIN_SCROLLABLE_PX = 200;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function LeadCaptureModal() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const waitlistOpen = LOCAL_PREVIEW_ENABLED || (registrationStatus?.waitlistEnabled ?? false);

  const [reachedBottom, setReachedBottom] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  // trigger d'abbandono: scroll vicino al fondo pagina (one-shot)
  useEffect(() => {
    if (reachedBottom) return;
    const onScroll = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll < MIN_SCROLLABLE_PX) return; // pagina non scrollabile → niente popup
      if (window.scrollY >= maxScroll * NEAR_BOTTOM_RATIO) setReachedBottom(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [reachedBottom]);

  // decide whether to open
  useEffect(() => {
    if (open) return;
    let lastDismissISO: string | null = null;
    try {
      lastDismissISO = localStorage.getItem(DISMISS_KEY);
    } catch {
      /* storage non disponibile */
    }
    const eligible = shouldShowLeadPopup({
      isAuthenticated,
      registrationOpen: waitlistOpen,
      reachedBottom,
      todayISO: todayISO(),
      lastDismissISO,
    });
    // non aprire se un'altra modale è già aperta (no stacking)
    if (eligible && !document.querySelector('[role="dialog"]')) {
      setOpen(true);
    }
  }, [isAuthenticated, waitlistOpen, reachedBottom, open]);

  function markDismissed() {
    try {
      localStorage.setItem(DISMISS_KEY, todayISO());
    } catch {
      /* ignora */
    }
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      markDismissed();
      setOpen(false);
    }
  }

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && consent && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const clean = email.trim().toLowerCase();
    try {
      await captureLeadPopup({ email: clean, marketingConsent: true });
    } catch {
      /* best-effort: prosegui comunque alla registrazione */
    }
    markDismissed();
    setOpen(false);
    navigate(`/register?email=${encodeURIComponent(clean)}`);
  }

  return (
    <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalPortal>
        <ModalOverlay className="bg-[#231F55]/40 backdrop-blur-lg" />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => { e.preventDefault(); emailRef.current?.focus(); }}
          className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] max-w-[420px] translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-2xl border border-white/10 shadow-2xl focus:outline-none"
          style={{ background: 'linear-gradient(160deg, #231F55 0%, #1a1740 55%, #2a2660 100%)' }}
          aria-labelledby="lead-popup-title"
        >
          <ModalClose className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-teal/40">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
            <span className="sr-only">Chiudi</span>
          </ModalClose>

          <div className="px-6 py-7 text-center sm:px-8">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-brand-orange">
              <span aria-hidden="true">● </span>Solo per te
            </p>
            <ModalTitle id="lead-popup-title" className="mt-3 font-serif text-2xl text-white">
              Aspetta un <span className="italic text-brand-orange">momento!</span>
            </ModalTitle>
            <ModalDescription className="mt-2 text-sm text-white/80">
              Ti regaliamo 7 minuti di consulto, completamente gratis.
            </ModalDescription>

            <div className="relative mt-4 overflow-hidden rounded-xl">
              <img src="/images/hero-home.png" alt="" aria-hidden="true" className="h-28 w-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#231F55] to-transparent px-3 py-2 text-center font-serif text-sm italic text-white">
                Il tuo primo consulto ti aspetta
              </span>
            </div>

            <form onSubmit={handleSubmit} className="mt-5 space-y-3 text-left">
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua e-mail"
                autoComplete="email"
                className="h-11 w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 text-sm text-white placeholder:text-white/40 focus:border-brand-teal/50 focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-brand-teal/20"
              />
              <label className="flex items-start gap-2 text-xs text-white/60">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 bg-white/10 text-brand-teal focus:ring-brand-teal"
                />
                Accetto di ricevere comunicazioni da Klaro7 (promozioni, novità e messaggi dei nostri consulenti).
              </label>
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex h-12 w-full items-center justify-center rounded-lg bg-brand-orange text-sm font-bold uppercase tracking-wide text-white shadow-md transition-all hover:bg-brand-orange/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-[#231F55]"
              >
                Riscatta i 7 minuti
              </button>
            </form>
          </div>
        </DialogPrimitive.Content>
      </ModalPortal>
    </Modal>
  );
}
